import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnChanges,
  SimpleChanges,
  ViewChild,
} from "@angular/core";
import { DriverViewModel } from "@app/shared/models/drivers/DriverViewModel.model";
import { Team } from "@app/shared/models/Team";
import { TeamsService } from "@app/teams/shared/teams.service";
import { DriverService } from "@app/driver/shared/driver.service";
import { ToastrService } from "ngx-toastr";
import { ConfirmationService } from "primeng/api";

@Component({
  selector: "app-driver-teams",
  templateUrl: "./driver-teams.component.html",
  styleUrls: ["./driver-teams.component.css"],
})
export class DriverTeamsComponent implements OnInit, OnChanges {
  @Input() driver: DriverViewModel;
  @Input() isEditMode: boolean;
  @Output() hasChanges = new EventEmitter<boolean>();
  @Output() teamSaved = new EventEmitter<void>();

  @ViewChild("closebutton") closebutton: {
    nativeElement: { click: () => void };
  };

  team: Team | null = null;
  totalPercent: number = 0;
  editing: boolean = false;
  creating: boolean = false;
  loading: boolean = false;
  teamSaving: boolean = false;

  // Available drivers for adding to team
  availableDrivers: DriverViewModel[] = [];
  selectedDriver: DriverViewModel | null = null;

  constructor(
    private teamsService: TeamsService,
    private driverService: DriverService,
    private toastr: ToastrService,
    private confirmationService: ConfirmationService
  ) {}

  ngOnInit() {
    this.loadTeamInfo();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes["driver"] && !changes["driver"].firstChange) {
      this.loadTeamInfo();
    }
  }

  loadTeamInfo() {
    if (this.driver?.teamId) {
      this.loading = true;
      this.teamsService.getTeam(this.driver.teamId).subscribe(
        (team) => {
          this.team = team;
          this.calculateTotalPercent();
          this.loading = false;
        },
        (error) => {
          console.error("Error loading team:", error);
          this.loading = false;
        }
      );
    } else {
      this.team = null;
    }
  }

  loadAvailableDrivers() {
    this.driverService.getTeamlessDrivers().subscribe(
      (drivers: any) => {
        this.availableDrivers = drivers
          .map((driver: DriverViewModel) => ({
            ...driver,
            itemName: `${driver.lastName}, ${driver.name}`,
          }))
          .filter(
            (driver: DriverViewModel) =>
              driver.lastName !== null &&
              driver.id !== this.driver.id &&
              (!this.team || !this.team.drivers.find((d) => d.id === driver.id))
          )
          .sort((a, b) => (a.lastName || "").localeCompare(b.lastName || ""));
      },
      (error) => {
        console.error("Error loading drivers:", error);
      }
    );
  }

  calculateTotalPercent() {
    if (!this.team) {
      this.totalPercent = 0;
      return;
    }
    this.totalPercent = this.team.drivers.reduce(
      (acc: number, curr: DriverViewModel) => acc + (curr.earningPercentage || 0),
      0
    );
  }

  startCreatingTeam() {
    this.creating = true;
    this.editing = true;
    this.team = {
      name: `${this.driver.lastName} Team`,
      drivers: [
        {
          ...this.driver,
          earningPercentage: 100,
          isPayTo: true,
        },
      ],
      type: this.driver.type,
    };
    this.calculateTotalPercent();
    this.loadAvailableDrivers();
  }

  cancelCreate() {
    this.creating = false;
    this.editing = false;
    this.team = null;
    this.totalPercent = 0;
  }

  startEditing() {
    this.editing = true;
    this.loadAvailableDrivers();
  }

  cancelEdit() {
    this.editing = false;
    this.loadTeamInfo();
  }

  validateTeam(): boolean {
    if (!this.team || !this.team.drivers || this.team.drivers.length < 2) {
      this.toastr.warning("Team must have at least 2 drivers");
      return false;
    }
    if (this.totalPercent !== 100) {
      this.toastr.warning("Total percentage must equal 100%");
      return false;
    }
    const numPayTos = this.team.drivers.filter((driver) => driver.isPayTo).length;
    if (numPayTos !== 1) {
      this.toastr.warning("There must be exactly one pay to driver");
      return false;
    }
    if (!this.team.name || this.team.name.trim() === "") {
      this.toastr.warning("Team name is required");
      return false;
    }
    return true;
  }

  createTeam(onComplete?: () => void) {
    if (!this.validateTeam()) {
      if (onComplete) onComplete();
      return;
    }

    this.teamSaving = true;
    this.teamsService.createTeam(this.team!).subscribe(
      (response) => {
        this.toastr.success("Team created successfully");
        this.creating = false;
        this.editing = false;
        this.teamSaving = false;
        // Reload driver to get updated team info
        this.driver.teamId = response.id || response.uuid;
        this.loadTeamInfo();
        // Emit event so parent can reload driver data with updated earning percentages
        this.teamSaved.emit();
        if (onComplete) onComplete();
      },
      (error) => {
        this.toastr.error("Error creating team");
        console.error(error);
        this.teamSaving = false;
        if (onComplete) onComplete();
      }
    );
  }

  saveTeam(onComplete?: () => void) {
    if (!this.validateTeam()) {
      if (onComplete) onComplete();
      return;
    }

    this.teamSaving = true;
    this.teamsService.updateTeam(this.team!).subscribe(
      () => {
        this.toastr.success("Team updated successfully");
        this.editing = false;
        this.teamSaving = false;
        this.loadTeamInfo();
        // Emit event so parent can reload driver data with updated earning percentages
        this.teamSaved.emit();
        if (onComplete) onComplete();
      },
      (error) => {
        this.toastr.error("Error updating team");
        console.error(error);
        this.teamSaving = false;
        if (onComplete) onComplete();
      }
    );
  }

  leaveTeam() {
    this.confirmationService.confirm({
      message: "Are you sure you want to leave this team? This will disband the entire team.",
      header: "Leave Team",
      icon: "pi pi-exclamation-triangle",
      accept: () => {
        if (!this.team?.id && !this.team?.uuid) return;

        this.teamSaving = true;
        const teamId = this.team.id || this.team.uuid;
        this.teamsService.deleteTeam(teamId!).subscribe(
          () => {
            this.toastr.success("Team disbanded successfully");
            this.driver.teamId = null;
            this.team = null;
            this.teamSaving = false;
          },
          (error) => {
            this.toastr.error("Error leaving team");
            console.error(error);
            this.teamSaving = false;
          }
        );
      },
    });
  }

  openAddDriverModal() {
    this.selectedDriver = null;
    this.loadAvailableDrivers();
  }

  addDriver() {
    if (!this.selectedDriver || !this.team) return;

    const driverToAdd = {
      ...this.selectedDriver,
      earningPercentage: 0,
      isPayTo: false,
    };

    this.team.drivers.push(driverToAdd);
    this.team.type = this.team.type || this.selectedDriver.type;
    this.calculateTotalPercent();
    this.closeModal();
  }

  removeDriver(driver: DriverViewModel) {
    if (!this.team) return;

    // Can't remove the driver being viewed
    if (driver.id === this.driver.id) {
      this.toastr.warning("Cannot remove this driver from the team. Use 'Leave Team' instead.");
      return;
    }

    this.team.drivers = this.team.drivers.filter((d) => d.id !== driver.id);
    this.calculateTotalPercent();
  }

  closeModal() {
    if (this.closebutton) {
      this.closebutton.nativeElement.click();
    }
  }

  onPayToChange(selectedDriver: DriverViewModel) {
    if (!this.team || !selectedDriver.isPayTo) return;

    // Deselect PayTo from all other drivers
    for (const d of this.team.drivers) {
      if (d.id !== selectedDriver.id) {
        d.isPayTo = false;
      }
    }
  }

  getDisplayType(type: string): string {
    if (type === "LEASE OPERATOR") {
      return "LESSOR";
    }
    return type;
  }
}
